import { Component, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { TableColumn } from 'src/app/components/common/common-table/common-table.component';
import {
  FilterBuilderOutput,
  FilterBuilderParam
} from 'src/app/models/filter-builder';
import {
  Utilities,
  WorkflowContextService,
  DataService
} from 'src/app/services';
import { PagedPermitSummaryReportResponse } from 'src/app/models/permits-reports';
import { Router, ActivatedRoute } from '@angular/router';
import { ComponentIds } from 'src/app/models/component-ids';
import { ColumnOption } from 'src/app/models/grid-settings';
import { WorkflowType } from 'src/app/models';

@Component({
  selector: 'app-permits-summary',
  templateUrl: './permits-summary.component.html',
  styleUrls: ['./permits-summary.component.css'],
  standalone: false
})
export class PermitSummaryComponent implements OnInit, OnChanges {
  id = ComponentIds.PERMITS_SUMMARY;
  loading = true;
  title = 'Summary';
  errorMessage = '';
  report: PagedPermitSummaryReportResponse;
  filterTypes = [
    {
      name: 'contains'
    },
    {
      name: 'is'
    },
    {
      name: 'in'
    },
    {
      name: 'range'
    }
  ];
  params: FilterBuilderParam[] = [
    {
      name: 'App Type',
      id: 'workflowName',
      types: ['is', 'in', 'contains'],
      options: []
    },
    {
      name: 'Date Created',
      id: 'timeframe',
      inputType: 'date',
      types: ['range']
    }
  ];
  filters: FilterBuilderOutput = {};
  public useExcelFormat: boolean;
  public sortField: string;
  public sortDescending = false;
  lastQuery = {};
  rows: any[];
  public page = {
    // The number of elements in the page
    size: 10,
    // The total number of elements
    totalElements: 0,
    // The total number of pages
    totalPages: 0,
    // The current page number
    pageNumber: 0
  };

  timeFrame: FilterBuilderParam = {
    name: 'Paid Date',
    id: 'timeframe',
    inputType: 'date',
    types: ['range']
  };

  totals: any[];

  columns: TableColumn[];
  availableColumns: TableColumn[];
  columnOptions: ColumnOption[] = [];
  exportColumns: string[];

  private readonly PAGE_SIZE_STORAGE_KEY =
    'wm-common-table-page-size-report-contractor-fee-summary-janus';
  constructor(
    private dataSvc: DataService,
    private toast: ToastrService,
    private _context: WorkflowContextService,
    public router: Router,
    public route: ActivatedRoute
  ) {}

  ngOnInit() {
    const storedPageSize = localStorage.getItem(this.PAGE_SIZE_STORAGE_KEY);

    if (storedPageSize) {
      const pageSize = Number(storedPageSize);

      if ([10, 25, 100, 200].includes(pageSize)) {
        this.page.size = pageSize;
      }
    }

    this.columns = [
      {
        key: 'workflow',
        label: 'App Type',
        headerType: 'text',
        cellType: 'text',
        showExportOption: true
      },
      {
        key: 'amount',
        label: 'Amount',
        headerType: 'text',
        cellType: 'text',
        showExportOption: true
      },
      {
        key: 'completed',
        label: 'Completed',
        headerType: 'text',
        cellType: 'text',
        showExportOption: true
      },
      {
        key: 'started',
        label: 'Started',
        headerType: 'text',
        cellType: 'text',
        showExportOption: true
      },
      {
        key: 'client',
        label: 'Client',
        headerType: 'text',
        cellType: 'text',
        showExportOption: true
      }
    ];

    this.columnOptions = this.columns.map(c => ({
      key: c.key,
      name: c.key,
      label: c.label,
      checked: !c.hidden,
      includeInExport: c.showExportOption ?? true
    }));

    this.setAvailableColumns();
    this.getApplications();
    // this.getReport();
  }

  setAvailableColumns() {
    this.availableColumns = this.columns;
  }

  isEquivalent(a: any, b: any) {
    const aProps = Object.getOwnPropertyNames(a);
    const bProps = Object.getOwnPropertyNames(b);

    if (aProps.length !== bProps.length) {
      return false;
    }

    for (let i = 0; i < aProps.length; i++) {
      const propName = aProps[i];
      if (a[propName] !== b[propName]) {
        return false;
      }
    }

    return true;
  }

  getApplications() {
    this.dataSvc
      .getWorkflows(this._context.client, true, [
        WorkflowType.ContractorRegistration,
        WorkflowType.ContractorRenewal,
        WorkflowType.Other,
        WorkflowType.Permit,
        WorkflowType.Renewable,
        WorkflowType.MobileInspection
      ])
      .subscribe(workflows => {
        const param = this.params.find(p => p.id === 'workflowName');
        param.options = workflows.map(w => w.version.name).sort();
      });
  }

  getQuery() {
    const timeframe = this.filters['timeframe'];
    const workflowName = this.filters['workflowName'];

    const query = {
      clientId: this._context.client.id,
      timeframe: timeframe && timeframe.value,
      workflowName: workflowName && workflowName.value,
      useExcelFormat: this.useExcelFormat,
      sortField: this.sortField,
      sortDescending: this.sortDescending,
      exportColumns: this.exportColumns
    };

    return query;
  }

  private getPagedQuery() {
    const timeframe = this.filters['timeframe'];
    const workflowName = this.filters['workflowName'];

    const query = {
      clientId: this._context.client.id,
      timeframe: timeframe && timeframe.value,
      applicationName: workflowName && workflowName.value,
      workflowName: workflowName && workflowName.value,
      pageNumber: this.page.pageNumber,
      pageSize: this.page.size,
      sortField: this.sortField,
      sortDescending: this.sortDescending
    };

    return query;
  }

  getReport() {
    const query = this.getPagedQuery();
    this.loading = true;
    this.dataSvc.getPagedPermitSummaryReport(query).subscribe({
      next: report => {
        this.page.totalElements = report.recordCount;
        this.page.totalPages = report.pageCount;
        this.rows = report.items.map(i => {
          i['workflow'] = i.workflowName;
          i['amount'] = Utilities.formatCurrency(i.amount);
          i['completed'] = i.completedCount;
          i['started'] = i.startedCount;
          i['client'] = i.clientName;
          return i;
        });
        this.report = report;
        this.totals = [
          { label: 'Total Fees Paid', value: `$${this.report.totalAmount}` },
          {
            label: 'Completed Applications',
            value: this.report.totalCompletedCount
          },
          {
            label: 'Started Applications',
            value: this.report.totalStartedCount
          }
        ];
        this.loading = false;
      },
      error: err => {
        console.error(err);
        this.loading = false;
        this.errorMessage = 'Failed to load report. Please try again.';
      }
    });
  }

  exportExcel() {
    this.useExcelFormat = true;
    this.export();
  }

  exportCsv() {
    this.useExcelFormat = false;
    this.export();
  }

  export() {
    const query = this.getQuery();
    this.dataSvc.exportPermitSummaryReport(query).subscribe(
      data => {
        if (this.useExcelFormat) {
          const blob = new Blob([data], {
            type:
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          });
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = `permits-summary-report-export-${new Date().getTime()}.xlsx`;
          link.click();
        } else {
          const blob = new Blob([data], { type: 'text/csv' });
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = `permits-summary-report-export-${new Date().getTime()}.csv`;
          link.click();
        }
      },
      err => {
        this.toast.error('Problem while downloading the file.');
        console.error(err);
      }
    );
  }

  onSortChanged(event: {
    sortField: string | null;
    sortDescending: boolean | null;
  }): void {
    if (event.sortField === null) {
      // reset to default sorting
      this.sortField = 'workflow'; // choose your default
      this.sortDescending = false;
    } else {
      this.sortField = event.sortField;
      this.sortDescending = event.sortDescending ?? false;
    }
    this.page.pageNumber = 0;
    this.getReport();
  }

  onRowsPerPageChange(size: number) {
    const pageSize = Number(size) || 10;
    localStorage.setItem(this.PAGE_SIZE_STORAGE_KEY, String(pageSize));
    this.page.size = pageSize;
    this.page.pageNumber = 0;
    this.getReport();
  }

  onPageChange(page: number) {
    this.page.pageNumber = page;
    this.getReport();
  }

  onRetry() {
    this.getReport();
  }

  onExportColumnsChange(cols: string[]) {
    this.exportColumns = cols;
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.getReport();
  }

  onFiltersChanged(filters: FilterBuilderOutput) {
    this.filters = filters;
    this.page.pageNumber = 0; // reset to first page
    this.getReport();
  }

  onColumnsChanged(columnSet: { columns: any[]; exportColumns: any[] }) {
    // Update visible columns
    const visibleKeys = columnSet.columns.map(c => c.name);

    this.columns = this.availableColumns.map(col => ({
      ...col,
      hidden: !visibleKeys.includes(col.key)
    }));

    // Update export columns
    this.exportColumns = columnSet.exportColumns.map(c => c.name);
  }
}
